import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import { SetupLinkedInGrowthActionsResponse } from '@dreamai/types';
import api from '../../../api';

import { ReactComponent as LinkedInIcon } from '../../../icons/linkedin-blue.svg';
import { ReactComponent as InstagramIcon } from '../../../icons/instagram.svg';
import { ReactComponent as ArrowLeftIcon } from '../../../icons/arrow-left.svg';
import { ReactComponent as ArrowRightIcon } from '../../../icons/arrow-right.svg';
import { ReactComponent as CheckIcon } from '../../../icons/check.svg';
import { ReactComponent as DocumentIcon } from '../../../icons/document.svg';

import './campaign-actions-modal.scss';

type ModalMode = 'dual' | 'linkedin' | 'instagram';

interface CampaignActionsModalProps {
  show: boolean;
  mode: ModalMode;
  onClose: () => void;
  onSetupComplete: () => void;
  onInstagramSetup: () => void;
  campaignId: number;
  threadId?: string;
  agentNickname: string;
  companyName?: string;
}

type LinkedInTier = 'basic' | 'premium';

const CampaignActionsModal: React.FC<CampaignActionsModalProps> = ({
  show,
  mode: initialMode,
  onClose,
  onSetupComplete,
  onInstagramSetup,
  campaignId,
  threadId,
  agentNickname,
  companyName = 'Your Company',
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();
  const [currentMode, setCurrentMode] = useState<ModalMode>(initialMode);
  const [selectedLinkedInTier, setSelectedLinkedInTier] =
    useState<LinkedInTier>('basic');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasPremiumAccess, setHasPremiumAccess] = useState<boolean | null>(
    null,
  );

  // Reset mode when modal opens with new mode
  useEffect(() => {
    if (show) {
      setCurrentMode(initialMode);
    }
  }, [show, initialMode]);

  // Fetch subscription data to check for premium access
  useEffect(() => {
    const fetchSubscriptionData = async () => {
      try {
        const token = await getAccessTokenSilently();
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        const response = await api.get('/subscription-details');
        const product = response.data.current_subscription_product;
        const hasAccess =
          product?.metadata?.linkedin_direct_associate === 'true';
        setHasPremiumAccess(hasAccess);
      } catch (error) {
        console.error('Error fetching subscription data:', error);
        setHasPremiumAccess(false);
      }
    };

    if (show) {
      fetchSubscriptionData();
    }
  }, [show, getAccessTokenSilently]);

  const handleUpgradeClick = () => {
    onClose();
    navigate('/settings/subscription');
  };

  const handleBack = () => {
    setCurrentMode('dual');
  };

  const handleLinkedInSetup = () => {
    setCurrentMode('linkedin');
  };

  const handleInstagramSetup = () => {
    setCurrentMode('instagram');
  };

  const handleFinishLinkedInSetup = async () => {
    if (isSubmitting || !threadId) return;

    try {
      setIsSubmitting(true);
      const token = await getAccessTokenSilently();
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const response = await api.post<SetupLinkedInGrowthActionsResponse>(
        `/campaigns/${campaignId}/setup-linkedin`,
        { threadId, tier: selectedLinkedInTier },
      );

      if (response.data.success) {
        onSetupComplete();
      } else {
        console.error('Failed to setup LinkedIn:', response.data.message);
      }
    } catch (error) {
      console.error('Error triggering LinkedIn setup:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInstagramActionsSetup = () => {
    onClose();
    onInstagramSetup();
  };

  const renderDualMode = () => (
    <>
      <Modal.Header>
        <Modal.Title>Setup Campaign Actions</Modal.Title>
        <button className='cancel-btn' onClick={onClose}>
          Cancel
        </button>
      </Modal.Header>

      <Modal.Body>
        <p className='modal-subtitle'>Choose a platform to setup.</p>

        <div className='platform-options'>
          <div className='platform-option linkedin-option'>
            <div className='platform-option-icon'>
              <LinkedInIcon />
            </div>
            <h3 className='platform-option-title'>Setup LinkedIn</h3>
            <p className='platform-option-description'>
              Choose a package, then setup LinkedIn. {agentNickname} will use
              their team to engage with customers, add likes, and comment.
            </p>
            <div className='platform-option-btn-container'>
              <button
                className='platform-option-btn'
                onClick={handleLinkedInSetup}
              >
                Setup LinkedIn Actions
              </button>
            </div>
          </div>

          <div className='platform-option instagram-option'>
            <div className='platform-option-icon'>
              <InstagramIcon />
            </div>
            <h3 className='platform-option-title'>Setup Instagram</h3>
            <p className='platform-option-description'>
              Once you've setup {agentNickname} will be able to like, comment,
              and DM on your behalf. You'll be able to review all text content.
            </p>
            <div className='platform-option-btn-container'>
              <button
                className='platform-option-btn'
                onClick={handleInstagramSetup}
              >
                Setup Instagram Actions
              </button>
            </div>
          </div>
        </div>
      </Modal.Body>
    </>
  );

  const renderLinkedInMode = () => (
    <>
      <Modal.Header>
        <div className='modal-header-with-back'>
          <button className='back-btn' onClick={handleBack}>
            <ArrowLeftIcon />
          </button>
          <Modal.Title>
            <span>Setup LinkedIn</span>
            <LinkedInIcon className='title-icon' />
          </Modal.Title>
        </div>
        <button className='cancel-btn' onClick={onClose}>
          Cancel
        </button>
      </Modal.Header>

      <Modal.Body>
        <div className='linkedin-tiers'>
          <div
            className={`linkedin-tier basic-tier ${selectedLinkedInTier === 'basic' ? 'selected' : ''}`}
            onClick={() => setSelectedLinkedInTier('basic')}
          >
            <div className='tier-header'>
              <div
                className={`tier-check ${selectedLinkedInTier === 'basic' ? 'selected' : ''}`}
              >
                {selectedLinkedInTier === 'basic' && <CheckIcon />}
              </div>
              <button
                className={`tier-select-btn ${selectedLinkedInTier === 'basic' ? 'selected' : ''}`}
              >
                {selectedLinkedInTier === 'basic' ? 'Selected' : 'Choose Basic'}
              </button>
            </div>

            <div className='tier-title-row'>
              <h4 className='tier-title'>Basic Actions</h4>
              <span className='tier-price free'>Free</span>
            </div>

            <p className='tier-description'>
              Our revenue partner introduces themselves to prospects as working
              with <strong>{companyName}</strong> and requests warm intro calls
              on your behalf.
            </p>
            <p className='tier-description'>
              Great for testing demand with zero setup.
            </p>

            <div className='tier-preview'>
              <img
                className='tier-preview-image'
                src='/campaigns/revenue-partner.png'
                alt='Basic tier preview'
              />
            </div>
          </div>

          <div
            className={`linkedin-tier premium-tier ${selectedLinkedInTier === 'premium' ? 'selected' : ''} ${!hasPremiumAccess ? 'locked' : ''}`}
            onClick={() =>
              hasPremiumAccess && setSelectedLinkedInTier('premium')
            }
          >
            {!hasPremiumAccess && (
              <div className='upgrade-banner'>
                <span>Upgrade to unlock</span>
              </div>
            )}
            <div className='tier-header'>
              <div
                className={`tier-check ${selectedLinkedInTier === 'premium' ? 'selected' : ''}`}
              >
                {selectedLinkedInTier === 'premium' && <CheckIcon />}
              </div>
              {hasPremiumAccess ? (
                <button
                  className={`tier-select-btn ${selectedLinkedInTier === 'premium' ? 'selected' : ''}`}
                >
                  {selectedLinkedInTier === 'premium'
                    ? 'Selected'
                    : 'Choose Premium'}
                </button>
              ) : (
                <button
                  className='tier-select-btn upgrade'
                  onClick={handleUpgradeClick}
                >
                  Upgrade package
                </button>
              )}
            </div>
            <div
              className={`tier-content ${!hasPremiumAccess ? 'locked' : ''}`}
            >
              <div className='tier-title-row'>
                <h4 className='tier-title'>Premium Actions</h4>
              </div>

              <p className='tier-description'>
                A dedicated revenue partner is listed as part of your team on
                LinkedIn, warming prospects through likes and pre-approved
                comments before starting conversations.
              </p>
              <p className='tier-description'>
                Looks, sounds, and behaves like an in-house SDR.
              </p>
              <p className='tier-description'>
                Higher trust, higher reply rates.
              </p>

              <div className='tier-preview'>
                <img
                  className='tier-preview-image'
                  src='/campaigns/direct-associate.png'
                  alt='Premium tier preview'
                />
              </div>
            </div>
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <button
          className='finish-setup-btn'
          onClick={handleFinishLinkedInSetup}
        >
          <span>Next: Finish setup</span>
          <ArrowRightIcon />
        </button>
      </Modal.Footer>
    </>
  );

  const renderInstagramMode = () => (
    <>
      <Modal.Header>
        <div className='modal-header-with-back'>
          <button className='back-btn' onClick={handleBack}>
            <ArrowLeftIcon />
          </button>
          <Modal.Title>
            <span>Setup Instagram</span>
            <InstagramIcon className='title-icon' />
          </Modal.Title>
        </div>
        <button className='cancel-btn' onClick={onClose}>
          Cancel
        </button>
      </Modal.Header>

      <Modal.Body>
        <div className='instagram-setup'>
          <div className='instagram-info'>
            <div className='info-topper'>
              <div className='instagram-tier-header'>
                <h4 className='tier-title'>Instagram Actions</h4>
                <span className='tier-price free'>Free</span>
              </div>

              <p className='tier-description'>
                We like, comment, and start conversations as your brand—directly
                from your official Instagram account—to warm prospects before
                any outreach.
              </p>
              <p className='tier-description'>
                Natural engagement that builds familiarity and trust in their
                feed and DMs.
              </p>
            </div>

            <div className='setup-guide-card'>
              <div className='setup-guide-icon'>
                <DocumentIcon />
              </div>
              <p className='setup-guide-text'>
                Follow our setup guide to get started
              </p>
              <div className='setup-guide-content'>
                <button
                  className='setup-instagram-btn'
                  onClick={handleInstagramActionsSetup}
                >
                  <span>Setup Instagram Actions</span>
                  <ArrowRightIcon />
                </button>
              </div>
            </div>
          </div>

          <div className='instagram-preview'>
            <img
              className='instagram-preview-image'
              src='/campaigns/insta-post.png'
              alt='Instagram preview'
            />
          </div>
        </div>
      </Modal.Body>
    </>
  );

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      size='lg'
      dialogClassName='campaign-actions-modal'
    >
      {currentMode === 'dual' && renderDualMode()}
      {currentMode === 'linkedin' && renderLinkedInMode()}
      {currentMode === 'instagram' && renderInstagramMode()}
    </Modal>
  );
};

export default CampaignActionsModal;
